// Chat on /workspace/:id. Each tab is a conversation with memory: generateWorkspaceOutput answers
// with the video, a rolling summary of older messages and the latest exchanges verbatim, and
// streams progress into users/{uid}/workspaceChats/workspace-{id}, which this listens to.
// A video includes messageLimit messages (by plan, see messageLimitFor), shared by all of its chats.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, updateDoc, deleteField, setDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faArrowDown, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

import { auth, db } from '../../../firebase';
import { registerListener } from '../../../components/appComponents/ListenerManager';
import ChatMessage from './ChatMessage';
import ChatComposer, { Allowance } from './ChatComposer';
import PlanPurchasePage from '../../../myPlans/PlanPurchasePage';
import {
  FREE_MESSAGE_LIMIT, PRO_MESSAGE_LIMIT, MAX_THREADS, buildThreads, countUsed, nextThreadId, newTurnId, ratingKey, sendErrorMessage,
} from './chatModel';
import './workspaceChat.css';

const SUGGESTIONS = [
  'Summarize the video',
  'What are the key takeaways?',
  'Write show notes with timestamps',
  'Quiz me with 5 questions',
];

// The backend can take several minutes on a long video; the progress itself arrives through Firestore.
const CALL_TIMEOUT_MS = 620 * 1000;

export default function WorkspaceChat({
  content_id, legacy, isChatReady, isProcessing, isScVideoPrivate, isYouTube,
  messageLimit = FREE_MESSAGE_LIMIT, isPro = false, isPreview = false, suggestions = SUGGESTIONS,
}) {
  const user = auth.currentUser;
  const contentId = `workspace-${content_id}`;
  const sendMessage = useMemo(
    () => httpsCallable(getFunctions(), 'generateWorkspaceOutput', { timeout: CALL_TIMEOUT_MS }),
    [],
  );

  const [chatDoc, setChatDoc] = useState(undefined); // undefined = loading, null = no messages yet
  const chatDocRef = useRef(chatDoc);
  chatDocRef.current = chatDoc;
  const [localThreadIds, setLocalThreadIds] = useState([]);
  const [optimistic, setOptimistic] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [errors, setErrors] = useState({});
  const [ratingOverrides, setRatingOverrides] = useState({});
  const [now, setNow] = useState(Date.now());

  // ── Listen to the chat doc ────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return undefined;
    setChatDoc(undefined);
    const unsub = onSnapshot(
      doc(db, 'users', user.uid, 'workspaceChats', contentId),
      snap => setChatDoc(snap.exists() ? snap.data() : null),
      () => setChatDoc(null),
    );
    registerListener(unsub);
    return () => unsub();
  }, [user, contentId]);

  // Re-evaluate busy/stale states once a minute.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Drop optimistic turns as soon as the server has recorded them.
  useEffect(() => {
    if (!chatDoc) return;
    setOptimistic(prev => {
      let changed = false;
      const next = { ...prev };
      Object.entries(prev).forEach(([turnId, o]) => {
        const turn = chatDoc.threads?.[o.threadId]?.turns?.[turnId];
        if (turn && (!o.retry || turn.status !== 'error')) { delete next[turnId]; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [chatDoc]);

  const loading = chatDoc === undefined || legacy === null;
  const threads = useMemo(
    () => (loading ? [] : buildThreads({ chatDoc, workspace: legacy || {}, localThreadIds, optimistic, now })),
    [loading, chatDoc, legacy, localThreadIds, optimistic, now],
  );
  const used = countUsed(threads);
  const remaining = Math.max(0, messageLimit - used);
  const [plansSource, setPlansSource] = useState(null); // where the plans modal was opened from

  // A workspace always shows at least one chat.
  useEffect(() => {
    if (!loading && threads.length === 0) setLocalThreadIds(['0']);
  }, [loading, threads.length]);

  // Open the most recent conversation first.
  useEffect(() => {
    if (loading || !threads.length) return;
    if (activeId === null || !threads.some(t => t.id === activeId)) {
      const withTurns = threads.filter(t => t.turns.length);
      setActiveId((withTurns[withTurns.length - 1] || threads[0]).id);
    }
  }, [loading, threads, activeId]);

  const active = threads.find(t => t.id === activeId) || null;

  // ── Scrolling: follow a new answer until it fills the view, then let the user read ─────
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const follow = useRef(true);
  const [showJump, setShowJump] = useState(false);

  const scrollToLatest = useCallback((smooth = false) => {
    const box = scrollRef.current;
    if (!box) return;
    const turns = box.querySelectorAll('.wsc-turn');
    const last = turns[turns.length - 1];
    const bottom = box.scrollHeight - box.clientHeight;
    const lastTop = last ? last.offsetTop - 16 : bottom;
    box.scrollTo({ top: Math.max(0, Math.min(bottom, lastTop)), behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  useLayoutEffect(() => {
    follow.current = true;
    scrollToLatest();
  }, [activeId, loading, scrollToLatest]);

  useEffect(() => {
    const box = scrollRef.current;
    const content = contentRef.current;
    if (!box || !content) return undefined;
    const stopFollowing = () => { follow.current = false; };
    const onScroll = () => setShowJump(box.scrollHeight - box.clientHeight - box.scrollTop > 240);
    const observer = new ResizeObserver(() => {
      if (follow.current) scrollToLatest();
      onScroll();
    });
    observer.observe(content);
    box.addEventListener('wheel', stopFollowing, { passive: true });
    box.addEventListener('touchmove', stopFollowing, { passive: true });
    box.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      box.removeEventListener('wheel', stopFollowing);
      box.removeEventListener('touchmove', stopFollowing);
      box.removeEventListener('scroll', onScroll);
    };
  }, [loading, scrollToLatest]);

  // ── Composer state ────────────────────────────────────────────────────────
  const textareaRef = useRef(null);
  const draft = (active && drafts[active.id]) || '';
  const setDraft = value => active && setDrafts(d => ({ ...d, [active.id]: value }));

  let unavailable = '';
  if (isScVideoPrivate) unavailable = 'Chat is unavailable. This video must be Public on YouTube.';
  else if (!isChatReady) unavailable = isProcessing ? 'Chat opens when your video finishes processing' : 'Add a video to start chatting';
  const busy = !!active && active.busy;
  const outOfMessages = remaining === 0 && !busy;

  // ── Send / retry ──────────────────────────────────────────────────────────
  const serverHasTurn = (threadId, turnId) => !!chatDocRef.current?.threads?.[threadId]?.turns?.[turnId];

  const send = async () => {
    if (!active || busy || unavailable || remaining <= 0) return;
    const prompt = draft.trim();
    if (!prompt) return;
    const threadId = active.id;
    const turnId = newTurnId();
    setOptimistic(o => ({ ...o, [turnId]: { threadId, prompt } }));
    setDrafts(d => ({ ...d, [threadId]: '' }));
    setErrors(e => ({ ...e, [threadId]: '' }));
    follow.current = true;
    try {
      await sendMessage({ contentId, threadId, turnId, message: prompt });
    } catch (err) {
      if (!serverHasTurn(threadId, turnId)) {
        setDrafts(d => ({ ...d, [threadId]: d[threadId] || prompt }));
        setErrors(e => ({ ...e, [threadId]: sendErrorMessage(err) }));
      }
    } finally {
      setOptimistic(o => {
        if (!o[turnId]) return o;
        const { [turnId]: _, ...rest } = o;
        return rest;
      });
    }
  };

  const retry = async turn => {
    if (!active || busy || remaining <= 0) return;
    const threadId = active.id;
    setOptimistic(o => ({ ...o, [turn.id]: { threadId, prompt: turn.prompt, retry: true } }));
    setErrors(e => ({ ...e, [threadId]: '' }));
    follow.current = true;
    try {
      await sendMessage({ contentId, threadId, turnId: turn.id, retry: true });
    } catch (err) {
      setErrors(e => ({ ...e, [threadId]: sendErrorMessage(err) }));
    } finally {
      setOptimistic(o => {
        if (!o[turn.id]) return o;
        const { [turn.id]: _, ...rest } = o;
        return rest;
      });
    }
  };

  const newChat = () => {
    const id = nextThreadId(threads);
    setLocalThreadIds(ids => [...ids, id]);
    setActiveId(id);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const pickSuggestion = text => {
    setDraft(text);
    setTimeout(() => {
      const el = textareaRef.current;
      if (el) { el.focus(); el.setSelectionRange(text.length, text.length); }
    }, 0);
  };

  // ── Ratings (workspace doc feedback_map + analytics) ──────────────────────
  const ratings = { ...(legacy?.feedback_map || {}), ...ratingOverrides };
  const rate = async (threadId, turn, newRating) => {
    if (!user) return;
    const key = ratingKey(threadId, turn);
    const previous = ratings[key] ?? null;
    if (previous === newRating) return;
    setRatingOverrides(r => ({ ...r, [key]: newRating }));
    try {
      await updateDoc(doc(db, 'users', user.uid, 'workspaceContent', contentId), {
        [`feedback_map.${key}`]: newRating ?? deleteField(),
      });
    } catch {
      setRatingOverrides(r => ({ ...r, [key]: previous }));
      return;
    }
    // Analytics — fire-and-forget; failures never revert the rating.
    setDoc(doc(db, 'workspaceFeedback', `${content_id}_${key}`), {
      userId: user.uid, contentId: content_id, promptId: key, threadId, turnId: turn.id,
      question: turn.prompt || '', answer: turn.answer || '', rating: newRating, timestamp: serverTimestamp(),
    }, { merge: true }).catch(() => {});
    const statsRef = doc(db, 'UsageStatistics', 'youtubeVideosStatisticsPostGemini');
    runTransaction(db, async t => {
      const snap = await t.get(statsRef);
      const sat = snap.exists() ? (snap.data().satisfaction ?? {}) : {};
      let likes = sat.totalLikes ?? 0;
      let dislikes = sat.totalDislikes ?? 0;
      if (previous === 'like') likes = Math.max(0, likes - 1);
      if (previous === 'dislike') dislikes = Math.max(0, dislikes - 1);
      if (newRating === 'like') likes += 1;
      if (newRating === 'dislike') dislikes += 1;
      const total = likes + dislikes;
      t.set(statsRef, {
        satisfaction: {
          totalLikes: likes, totalDislikes: dislikes,
          satisfactionRate: total > 0 ? Math.round((likes / total) * 1000) / 10 : 0,
          lastUpdated: serverTimestamp(),
        },
      }, { merge: true });
    }).catch(() => {});
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const canAddChat = threads.length < MAX_THREADS && remaining > 0 && !unavailable;
  const placeholder = unavailable
    || (busy ? 'Bump AI is answering…' : active && active.turns.length ? 'Ask a follow-up' : 'Ask anything about this video');

  return (
    <div className="wsc">
      <div className="wsc-head">
        <div className="wsc-tabs" role="tablist" aria-label="Chats">
          {threads.map(t => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={t.id === activeId}
              className={`wsc-tab ${t.id === activeId ? 'is-active' : ''}`}
              onClick={() => setActiveId(t.id)}
              title={t.name}
            >
              {t.busy && <span className="wsc-tab-live" aria-label="Answering" />}
              <span className="wsc-tab-name">{t.turns.length && t.name !== 'New Chat' ? t.name : 'New chat'}</span>
            </button>
          ))}
        </div>
        {threads.length < MAX_THREADS && active && active.turns.length > 0 && (
          <button
            type="button"
            className="wsc-new"
            onClick={newChat}
            disabled={!canAddChat}
            title={remaining > 0 ? 'Start a fresh conversation. It shares this video\'s messages.' : 'No messages left for this video'}
          >
            <FontAwesomeIcon icon={faPlus} /> New chat
          </button>
        )}
      </div>

      <div className="wsc-scroll" ref={scrollRef}>
        <div className="wsc-thread" ref={contentRef}>
          {loading ? (
            <div className="wsc-loading" aria-label="Loading chat"><span /><span /><span /></div>
          ) : active && active.turns.length > 0 ? (
            active.turns.map((turn, i) => (
              <ChatMessage
                key={turn.id}
                turn={turn}
                isYouTube={isYouTube}
                isLast={i === active.turns.length - 1}
                canRetry={!busy && remaining > 0 && !unavailable}
                onRetry={() => retry(turn)}
                rating={ratings[ratingKey(active.id, turn)] ?? null}
                onRate={r => rate(active.id, turn, r)}
              />
            ))
          ) : (
            <div className="wsc-empty">
              <h2 className="wsc-empty-title">Ask anything about this video</h2>
              <p className="wsc-empty-sub">
                Bump AI remembers the conversation, so you can follow up, refine an answer, or build on it.
              </p>
              {!unavailable && remaining > 0 && (
                <div className="wsc-suggestions">
                  {suggestions.map(s => (
                    <button key={s} type="button" className="wsc-suggestion" onClick={() => pickSuggestion(s)}>{s}</button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showJump && (
        <button type="button" className="wsc-jump" onClick={() => { follow.current = false; const b = scrollRef.current; b.scrollTo({ top: b.scrollHeight, behavior: 'smooth' }); }} aria-label="Jump to latest">
          <FontAwesomeIcon icon={faArrowDown} />
        </button>
      )}

      <div className="wsc-bottom">
        {active && errors[active.id] && (
          <div className="wsc-send-error" role="alert">
            <FontAwesomeIcon icon={faCircleExclamation} /> {errors[active.id]}
          </div>
        )}
        {outOfMessages && !unavailable ? (
          <div className="wsc-limit">
            <Allowance remaining={0} limit={messageLimit} />
            <p className="wsc-limit-title">
              {isPreview && !isPro
                ? `Your free preview includes ${messageLimit} messages.`
                : `You've used all ${messageLimit} messages for this video.`}
            </p>
            {isPro ? (
              <p className="wsc-limit-sub">Your chats stay here to read, copy, and rate.</p>
            ) : (
              <>
                <p className="wsc-limit-sub">
                  {isPreview
                    ? `Pro unlocks every chapter and ${PRO_MESSAGE_LIMIT} messages for each video.`
                    : `Pro includes ${PRO_MESSAGE_LIMIT} messages for each video. Your chats stay here either way.`}
                </p>
                <button type="button" className="wsc-limit-upgrade" onClick={() => setPlansSource(isPreview ? 'chat_preview_limit' : 'chat_limit')}>
                  Upgrade to Pro
                </button>
              </>
            )}
          </div>
        ) : (
          <ChatComposer
            value={draft}
            onChange={setDraft}
            onSend={send}
            textareaRef={textareaRef}
            placeholder={placeholder}
            disabled={!!unavailable || loading}
            sendBlocked={busy}
            remaining={remaining}
            limit={messageLimit}
          />
        )}
      </div>

      {plansSource && <PlanPurchasePage source={plansSource} onClose={() => setPlansSource(null)} />}
    </div>
  );
}
